import { Device } from '@services/device'
import { Button } from '@shared/innmaxUI/Form'
import { Spin } from 'antd'
import cx from 'classnames'
import { pathOr } from 'ramda'
import React from 'react'
import Draggable from 'react-draggable'
import { useTranslation } from 'react-i18next'
import ReactModal, { Props } from 'react-modal'
import styled from 'styled-components'
import IconClose from './icons/close'
import IconFullscreen from './icons/fullscreen'

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'vortex-streaming': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        'api-token'?: string
        'device-id'?: string
        volume?: string
        muted?: boolean
        'auto-play'?: string
        class?: string
      }
    }
  }
}

export type CameraModalProps = {
  device?: Device
  onRequestClose?: () => void
  onClose?: () => void
} & Props

export default function CameraModal({
  device,
  onRequestClose,
  onClose,
  ...props
}: CameraModalProps) {
  const { t } = useTranslation()

  const [isFullscreen, setIsFullscreen] = React.useState(false)

  const [isPlaying, setIsPlaying] = React.useState(true)
  const [isLoading, setIsLoading] = React.useState(true)
  const [errorMsg, setErrorMsg] = React.useState<string>('')

  const toggleFullScreen = () => {
    setIsFullscreen(!isFullscreen)
  }

  const handleRequestClose = (e: React.MouseEvent) => {
    setIsPlaying(false)
    if (onClose) {
      return onClose()
    }
    onRequestClose && onRequestClose(e)
  }

  const id = `vortexStreaming${pathOr('', ['id'], device)}`

  const renderLoading = () => {
    if (!isLoading) {
      return null
    }
    return (
      <div className="absolute top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-10">
        <Spin size="large" tip={t('common:loading')} spinning />
      </div>
    )
  }

  const renderError = () => {
    if (!errorMsg) {
      return null
    }
    return (
      <div className="absolute top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-10">
        <div className="bg-danger text-white w-full p-5 text-center rounded-full">
          {errorMsg}
        </div>
      </div>
    )
  }

  React.useEffect(() => {
    const playerReady = () => {
      console.log('player ready')
      setIsLoading(false)
    }

    setTimeout(() => {
      const element = document.getElementById(id)
      element?.addEventListener('play', () => {
        console.log('streaming started')
        playerReady()
      })
      element?.addEventListener('error', (e: any) => {
        console.error('streaming error', e.detail)
        setErrorMsg(`取得串流失敗(${e.detail})`)
        playerReady()
      })
    }, 0)

    return () => {
      const element = document.getElementById(id)
      element?.removeEventListener('play', () => {})
      element?.removeEventListener('error', () => {})
    }
  }, [id])

  return (
    <ReactModal
      {...props}
      shouldCloseOnOverlayClick={false}
      ariaHideApp={false}
      onRequestClose={handleRequestClose}
      style={{
        overlay: {
          zIndex: 1200,
          background: 'transparent',
          position: 'fixed',
          pointerEvents: 'none',
        },
        content: {
          padding: 0,
          background: 'transparent',
          border: 0,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...(isFullscreen && {
            width: '100%',
            height: '100%',
            borderRadius: 0,
          }),
        },
      }}>
      <Draggable
        cancel=".video-react-control-bar"
        handle=".modal-content"
        bounds="body">
        <ModalContent
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
          }}
          className={cx('modal-content', {
            'w-full h-full transform-none': isFullscreen,
          })}>
          <div className="flex justify-between items-center px-16 pt-16">
            <div className="text-white font-medium">
              <span className="text-[16px]">
                即時影像 {pathOr('', ['displayName'], device)}
              </span>
            </div>
            <div className="flex">
              <Button
                type="icon"
                title={isFullscreen ? '離開全螢幕' : '全螢幕'}
                onClick={toggleFullScreen}>
                <IconFullscreen style={{ width: 32, height: 32 }} />
              </Button>
              <Button
                type="icon"
                title="關閉"
                className="ml-8"
                onClick={handleRequestClose}>
                <IconClose style={{ width: 32, height: 32 }} />
              </Button>
            </div>
          </div>
          <div className="px-24 pt-14 pb-24">
            <div
              className="relative flex justify-center items-center"
              style={{
                height: isFullscreen ? 'calc(100vh - 80px)' : 420,
                width: '100%',
              }}>
              {renderLoading()}
              {renderError()}
              <vortex-streaming
                id={id}
                class="flex-1 h-full"
                api-token="sk-user-tbeHPIaUIYqqD/+DGnNWip+YP1TyUaKtyRhkMbt6oNI="
                device-id={pathOr('', ['properties', 'vortexDeviceId'], device)}
                volume="50"
                muted
                onClick={() => {
                  if (isPlaying) {
                    setIsPlaying(false)
                    // @ts-ignore
                    document.getElementById(id)?._instance?.exposed?.pause()
                  } else {
                    setIsPlaying(true)
                    // @ts-ignore
                    document.getElementById(id)?._instance?.exposed?.play()
                  }
                }}
                auto-play="true"></vortex-streaming>
            </div>
          </div>
        </ModalContent>
      </Draggable>
    </ReactModal>
  )
}

const ModalContent = styled.div`
  width: 608px;
  background: #222;
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  pointer-events: all;
  cursor: move;

  .video-react-big-play-button {
    display: none;
  }
`
